import React, { useState, useEffect } from 'react';
import { 
  Menu, X, LogOut, BookOpen, Users, QrCode, 
  Bell, Star, Heart, Calendar, Home, UserCircle 
} from 'lucide-react';

// ✅ CONEXIÓN AL SISTEMA NERVIOSO CENTRAL (Rutas Absolutas)
import { useGlobal } from 'context/GlobalContext';

// ✅ IMPORTACIÓN DE VISTAS (Rutas Absolutas)
import PerfilTutor from 'pages/padre/PerfilTutor';
import MisNinos from 'pages/padre/MisNinos';
import LlaveAcceso from 'pages/padre/LlaveAcceso';
import CentroNotificaciones from 'pages/padre/CentroNotificaciones';
import BitacoraConducta from 'pages/padre/BitacoraConducta';
import CuidadosEspeciales from 'pages/padre/CuidadosEspeciales';
import CalendarioActividades from 'pages/padre/CalendarioActividades';
import MiSalon from 'pages/padre/MiSalon';

const PadreModule = ({ alSalir }) => {
  // --- CONSUMO DE DATOS REALES ---
  const { ninos, alertasAdmin } = useGlobal();

  // --- ESTADOS ---
  const [vistaActiva, setVistaActiva] = useState('misNinos');
  const [perfilCompleto, setPerfilCompleto] = useState(true);
  const [menuAbierto, setMenuAbierto] = useState(false);
  const [versiculoDia, setVersiculoDia] = useState({ texto: '', cita: '' });

  // --- COLECCIÓN DE VERSÍCULOS (Empatía y Familia) ---
  const versiculos = [
    { texto: "Herencia de Jehová son los hijos; cosa de estima el fruto del vientre.", cita: "Salmos 127:3" },
    { texto: "Instruye al niño en su camino, y aun cuando fuere viejo no se apartará de él.", cita: "Proverbios 22:6" },
    { texto: "Dejad a los niños venir a mí, y no se lo impidáis; porque de los tales es el reino de Dios.", cita: "Marcos 10:14" },
    { texto: "Y todos tus hijos serán enseñados por Jehová; y se multiplicará la paz de tus hijos.", cita: "Isaías 54:13" },
    { texto: "Corona de los viejos son los nietos, y la honra de los hijos, sus padres.", cita: "Proverbios 17:6" }
  ];

  // Elegir un versículo al azar cada vez que el menú se abre
  useEffect(() => {
    if (menuAbierto) {
      const azar = Math.floor(Math.random() * versiculos.length);
      setVersiculoDia(versiculos[azar]);
    }
  }, [menuAbierto]);

  // --- OPCIONES DEL MENÚ ---
  const menuItems = [
    { id: 'misNinos', label: 'Mis Niños', icon: <Users size={28} /> },
    { id: 'llave', label: 'Llave Acceso', icon: <QrCode size={28} /> },
    { id: 'notificaciones', label: 'Avisos', icon: <Bell size={28} /> },
    { id: 'conducta', label: 'Conducta', icon: <Star size={28} /> },
    { id: 'cuidados', label: 'Cuidados', icon: <Heart size={28} /> },
    { id: 'calendario', label: 'Eventos', icon: <Calendar size={28} /> },
    { id: 'salon', label: 'Mi Salón', icon: <Home size={28} /> },
    { id: 'perfil', label: 'Mi Perfil', icon: <UserCircle size={28} /> },
  ];

  const cambiarVista = (id) => {
    setVistaActiva(id);
    setMenuAbierto(false); 
  };

  // --- RENDERIZADO CON PASO DE DATOS REALES ---
  const renderizarPantalla = () => {
    if (!perfilCompleto) return <PerfilTutor />;
    
    switch (vistaActiva) {
      case 'misNinos': return <MisNinos ninos={ninos} />;
      case 'llave': return <LlaveAcceso ninos={ninos} />;
      case 'notificaciones': return <CentroNotificaciones alertas={alertasAdmin} />;
      case 'conducta': return <BitacoraConducta ninos={ninos} />;
      case 'cuidados': return <CuidadosEspeciales ninos={ninos} />;
      case 'calendario': return <CalendarioActividades />;
      case 'salon': return <MiSalon ninos={ninos} />;
      case 'perfil': return <PerfilTutor />;
      default: return <MisNinos ninos={ninos} />;
    }
  };

  return (
    <div className="relative min-h-screen bg-slate-50 font-sans text-slate-900 flex flex-col overflow-hidden">
      
      {/* --- FONDO MARCA DE AGUA PROFESIONAL --- */}
      <div 
        className="fixed inset-0 z-0 pointer-events-none opacity-5"
        style={{
          backgroundImage: "url('/assets/logos/igle-kids.png')",
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat"
        }}
      />

      {/* --- BARRA SUPERIOR --- */}
      <header className="relative z-10 bg-white border-b-4 border-slate-200 sticky top-0 shadow-sm">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 h-24 flex items-center justify-between">
          
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 bg-slate-50 rounded-full overflow-hidden flex items-center justify-center border-2 border-slate-200 shadow-inner shrink-0">
              <img 
                src="/assets/logos/casa-de-fe.png" 
                alt="Casa de Fe" 
                className="w-full h-full object-cover"
                onError={(e) => { e.target.style.display = 'none'; }} 
              />
            </div>
            <div className="text-left">
              <h1 className="text-2xl sm:text-3xl font-black italic tracking-tighter text-slate-900 leading-none uppercase text-left">Casa de Fe</h1>
              <p className="text-xs font-black text-indigo-500 uppercase tracking-[0.3em] mt-1 text-left">IgleKids Seguro</p>
            </div>
          </div>

          <button 
            onClick={() => setMenuAbierto(true)} 
            className="p-3 sm:p-4 bg-slate-100 text-slate-700 rounded-2xl border-2 border-slate-200 hover:bg-indigo-100 hover:text-indigo-700 hover:border-indigo-200 transition-all active:scale-95 shadow-sm"
          >
            <Menu size={32} />
          </button>
        </div>
      </header>

      {/* --- CONTENIDO PRINCIPAL --- */}
      <main className="relative z-10 flex-1 w-full max-w-[1400px] mx-auto p-4 sm:p-6 md:p-10 text-left">
        <div className="animate-in fade-in slide-in-from-bottom-4 duration-700">
          {renderizarPantalla()}
        </div>
      </main>

      {/* --- MENÚ DESPLEGABLE LATERAL (DRAWER) --- */}
      {menuAbierto && (
        <div className="fixed inset-0 z-50 flex justify-end">
          
          <div 
            className="absolute inset-0 bg-slate-900/70 backdrop-blur-sm animate-in fade-in duration-300"
            onClick={() => setMenuAbierto(false)}
          />

          <div className="relative w-full max-w-md bg-white h-full shadow-2xl flex flex-col animate-in slide-in-from-right duration-300 border-l-8 border-indigo-600">
            
            <div className="p-6 sm:p-8 flex justify-between items-center border-b-4 border-slate-100 bg-slate-50">
              <h2 className="text-3xl font-black uppercase tracking-widest text-slate-900 text-left">Navegación</h2>
              <button 
                onClick={() => setMenuAbierto(false)} 
                className="p-3 bg-white text-slate-500 rounded-full border-2 border-slate-200 shadow-sm hover:bg-rose-50 hover:text-rose-600 hover:border-rose-200 transition-all"
              >
                <X size={28} />
              </button>
            </div>

            <nav className="flex-1 overflow-y-auto p-6 sm:p-8 space-y-4">
              {menuItems.map((opcion) => (
                <button
                  key={opcion.id}
                  onClick={() => cambiarVista(opcion.id)}
                  className={`w-full flex items-center gap-6 p-5 sm:p-6 rounded-3xl font-black text-xl transition-all border-4 ${
                    vistaActiva === opcion.id 
                      ? 'bg-indigo-600 text-white border-indigo-700 shadow-lg scale-[1.02]' 
                      : 'bg-white text-slate-600 border-slate-100 hover:border-indigo-200 hover:bg-indigo-50 hover:text-indigo-700'
                  }`}
                >
                  <div className={vistaActiva === opcion.id ? 'text-indigo-200' : 'text-slate-400'}>
                    {opcion.icon}
                  </div>
                  <span className="uppercase tracking-wide text-left">{opcion.label}</span>
                </button>
              ))}
            </nav>

            <div className="p-6 sm:p-8 bg-indigo-50 border-t-4 border-indigo-100">
              <div className="bg-white p-6 rounded-3xl border-2 border-indigo-100 shadow-sm space-y-4 relative overflow-hidden text-left">
                <BookOpen size={60} className="absolute -top-4 -right-4 text-indigo-50 opacity-50" />
                <div className="relative z-10">
                  <p className="text-base sm:text-lg text-indigo-900 font-bold leading-relaxed italic">
                    "{versiculoDia.texto}"
                  </p>
                  <p className="text-xs sm:text-sm text-indigo-600 font-black uppercase tracking-widest mt-4">
                    — {versiculoDia.cita}
                  </p>
                </div>
              </div>
              
              <button 
                onClick={alSalir}
                className="w-full mt-6 py-5 bg-white border-2 border-rose-200 rounded-2xl flex items-center justify-center gap-3 text-rose-600 font-black uppercase tracking-widest hover:bg-rose-600 hover:text-white transition-colors shadow-sm"
              >
                <LogOut size={24} /> Cerrar Sesión
              </button>
            </div>

          </div>
        </div>
      )}
    </div>
  );
};

export default PadreModule;